import { memo } from "react";
import { EGG_SVG_HEIGHT, EGG_SVG_WIDTH } from "../constants";
import Confetti from "./Confetti";
import { Egg } from "./Egg";

// ─── Reusable ghost button ────────────────────────────────────────────────────

interface OverlayButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  animationDelay?: string;
}

function OverlayButton({
  onClick,
  children,
  animationDelay = "0.35s",
}: OverlayButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "9px 26px",
        background: "transparent",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 5,
        color: "rgba(255,255,255,0.7)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 3,
        textTransform: "uppercase",
        cursor: "pointer",
        transition: "border-color 0.15s, color 0.15s, background 0.15s",
        animation: `winBadge 0.6s ${animationDelay} cubic-bezier(0.34,1.56,0.64,1) forwards`,
        opacity: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.06)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)";
        e.currentTarget.style.color = "#ffffff";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
        e.currentTarget.style.color = "rgba(255,255,255,0.7)";
      }}
    >
      {children}
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface WinScreenButton {
  onClick: () => void;
  label: string;
}

interface WinScreenProps {
  // ── Callbacks — button renders ONLY if provided ──
  onReplay?: () => void;
  onClose?: () => void;

  // ── Text overrides ──
  titleText?: string;
  descriptionText?: string;
  replayButtonText?: string;
  closeButtonText?: string;

  // ── Visual options ──
  showEgg?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

function WinScreenComponent({
  onReplay,
  onClose,

  titleText = "PAINTED!",
  descriptionText = "All keys pressed — egg complete",
  replayButtonText = "PLAY AGAIN",
  closeButtonText = "CLOSE",

  showEgg = true,
}: WinScreenProps) {
  const eggScale = 80 / EGG_SVG_WIDTH;
  const scaledW = EGG_SVG_WIDTH * eggScale;
  const scaledH = EGG_SVG_HEIGHT * eggScale;

  // Build ordered button list — only include buttons whose callback exists
  const buttons: WinScreenButton[] = (
    [
      onReplay && { onClick: onReplay, label: replayButtonText },
      onClose && { onClick: onClose, label: closeButtonText },
    ] as (WinScreenButton | false)[]
  ).filter(Boolean) as WinScreenButton[];

  return (
    <>
      <Confetti />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 50,
          background: "rgba(8,10,14,0.72)",
          backdropFilter: "blur(6px)",
        }}
        className="!rounded-3xl "
      >
        {/* ── Badge ── */}
        <div
          style={{
            animation: "winBadge 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards",
            opacity: 0,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
          className="!rounded-3xl "
        >
          {showEgg && (
            <div
              style={{
                width: scaledW,
                height: scaledH,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transform: `scale(${eggScale})`,
                  transformOrigin: "top left",
                }}
              >
                <Egg progress={100} shaking={false} invalid={false} />
              </div>
            </div>
          )}

          <div
            className="sporty-pro-font"
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: "#ffffff",
              letterSpacing: 6,
              textTransform: "uppercase",
              marginTop: 4,
            }}
          >
            {titleText}
          </div>

          <div
            style={{
              color: "rgba(255,255,255,0.38)",
              fontSize: 12,
              letterSpacing: 2,
              textTransform: "uppercase",
              animation: "winFloat 2s ease-in-out infinite",
            }}
          >
            {descriptionText}
          </div>
        </div>

        {/* ── Buttons row — staggered fade-in ── */}
        {buttons.length > 0 && (
          <div
            style={{
              marginTop: 28,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {buttons.map(({ onClick, label }, i) => (
              <OverlayButton
                key={label}
                onClick={onClick}
                animationDelay={`${0.35 + i * 0.08}s`}
              >
                {label}
              </OverlayButton>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export const WinScreen = memo(WinScreenComponent);
export default WinScreen;
